import { useConstant, useFn } from '@eturi/react'
import { rawSortedFilteredChildren$ } from '@op/services'
import { createSelector } from '@reduxjs/toolkit'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { composeValidators, maxLength, minLength, required } from '../../validators'
import { MAX_USERNAME_LENGTH, MIN_USERNAME_LENGTH } from '../../validators/constants'
import { createInputField, type OPInputFieldProps } from './createInputField'

export type ChildNameUser = {
	readonly name: string
	readonly id?: string
}

type ChildNameFieldProps = OPInputFieldProps & {
	readonly editingUserId?: string
	readonly users?: ChildNameUser[]
}

export const ChildNameField = ({ editingUserId, users: usersProp, ...p }: ChildNameFieldProps) => {
	const defaultChildNameUsers$ = useConstant(() =>
		createSelector(rawSortedFilteredChildren$, (users) =>
			users.map((u): ChildNameUser => ({ id: u.user_id, name: u.user_name || '' })),
		),
	)
	const defaultChildNameUsers = useSelector(defaultChildNameUsers$)

	const users = usersProp ?? defaultChildNameUsers

	const normalizedUsers = useMemo(
		() => users.map((u) => ({ ...u, name: trimLower(u.name) })),
		[users],
	)

	const hasDuplicateName = useFn((userName: Maybe<string>) => {
		const normalizedUserName = trimLower(userName)
		const hasSameId = editingUserId ? (u: ChildNameUser) => u.id === editingUserId : () => false
		const hasSameName = (u: ChildNameUser) => u.name === normalizedUserName

		return normalizedUsers.some((u) => !hasSameId(u) && hasSameName(u))
	})

	const UsernameFieldInput = useConstant(() => {
		const validateName = composeValidators(
			required('error_strings.empty_child_name'),
			minLength('error_strings.invalid_name', MIN_USERNAME_LENGTH),
			maxLength('error_strings.name_max_limit_error', MAX_USERNAME_LENGTH),
			(newUsername: Maybe<string>) =>
				hasDuplicateName(newUsername) ? 'error_strings.duplicate_child_name' : undefined,
		)

		return createInputField(
			{ name: 'name', validate: validateName },
			{ maxLength: MAX_USERNAME_LENGTH + 1, placeholder: 'form_controls.child_name.placeholder' },
			'OPUname',
		)
	})

	return <UsernameFieldInput {...p} />
}

const trimLower = (s: Maybe<string>) => (s || '').trim().toLowerCase()
